import {
  activityTaxonomyDefinition,
  type CustomActivityTaxonomySelection,
} from '../definitions/activityTaxonomy';
import {
  type Filter,
  FilterId,
  FilterType,
  isMultiFilter,
  isSingleValueFilter,
} from '../types/filters';
import type { TaxonomyConfig } from '../types/taxonomy';
import {
  describeTaxonomyRejection,
  type TaxonomyFilterRemoval,
} from './taxonomyFilterRemoval';

/**
 * A deeplink or saved view authored by a flag-on user can carry activity
 * filters into a flag-off session; the filters do not exist there
 * (createDefaultFilterMap never seeds them), so drop the entries rather than
 * restoring an un-recreatable chip.
 */
export const stripDisabledActivityFilters = (
  filterPairs: [FilterId, Filter][],
  taxonomyConfig?: TaxonomyConfig
): [FilterId, Filter][] =>
  taxonomyConfig?.activityTaxonomyEnabled === true
    ? filterPairs
    : filterPairs.filter(
        ([id]) =>
          id !== FilterId.ACTIVITY &&
          id !== FilterId.MULTI_ACTIVITY &&
          id !== FilterId.ACTIVITY_TAXONOMY
      );

const isActivityTaxonomySelection = (
  value: unknown
): value is CustomActivityTaxonomySelection => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'taxonomyId' in value &&
    typeof value.taxonomyId === 'number'
  );
};

export const validateActivityFilters = ({
  filters,
  taxonomyVersion,
  onRemoveFilter,
}: {
  filters: [FilterId, Filter][];
  taxonomyVersion?: string;
  onRemoveFilter?: (filterId: FilterId, removal: TaxonomyFilterRemoval) => void;
}): [FilterId, Filter][] => {
  const activityTaxonomyFilter = filters.find(
    ([id]) => id === FilterId.ACTIVITY_TAXONOMY
  )?.[1];

  if (!activityTaxonomyFilter || !isSingleValueFilter(activityTaxonomyFilter)) {
    return filters;
  }

  const value = activityTaxonomyFilter.value;
  const isValidValue = isActivityTaxonomySelection(value);
  const hasVersionMismatch =
    isValidValue &&
    taxonomyVersion !== undefined &&
    value.version !== taxonomyVersion;

  if (!isValidValue || hasVersionMismatch) {
    let didRemoveActivitySelections = false;
    const updatedFilters = filters.map(([id, filter]): [FilterId, Filter] => {
      // Reset the taxonomy filter itself (mirrors validateRoleFilters):
      // keeping the rejected taxonomyId/version would make the picker fetch
      // the tree at the exact version just declared invalid.
      if (id === FilterId.ACTIVITY_TAXONOMY) {
        return [
          id,
          {
            ...activityTaxonomyDefinition,
            value: {
              ...activityTaxonomyDefinition.value,
              version: taxonomyVersion,
            },
          },
        ];
      }

      // Also clear MULTI_ACTIVITY instances when the taxonomy is outdated:
      // activity ids are taxonomy-relative, so every AND-ed instance is stale.
      if (id === FilterId.MULTI_ACTIVITY) {
        if (
          !isMultiFilter(filter) ||
          filter.wrappedFilterId !== FilterId.ACTIVITY ||
          filter.instances.length === 0
        ) {
          return [id, filter];
        }
        didRemoveActivitySelections = true;
        return [id, { ...filter, instances: [] }];
      }

      if (id !== FilterId.ACTIVITY || filter.type !== FilterType.TREE) {
        return [id, filter];
      }

      if (filter.selections.length === 0) {
        return [id, filter];
      }

      didRemoveActivitySelections = true;
      return [
        id,
        {
          ...filter,
          primarySelections: [],
          selections: [],
        },
      ];
    });

    if (didRemoveActivitySelections) {
      onRemoveFilter?.(
        FilterId.ACTIVITY,
        describeTaxonomyRejection({
          value,
          isValidValue,
          currentVersion: taxonomyVersion,
        })
      );
    }

    return updatedFilters;
  }

  if (
    value.taxonomyId === 0 ||
    activityTaxonomyFilter.isUserSubmitted === true
  ) {
    return filters;
  }

  return filters.map(([id, filter]): [FilterId, Filter] => {
    return id === FilterId.ACTIVITY_TAXONOMY
      ? [id, { ...filter, isUserSubmitted: true }]
      : [id, filter];
  });
};
